import React, { useState } from 'react';
import { Share2, Copy, CheckCircle } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';

interface ShareButtonProps {
  topicId: string;
  topicTitle: string;
  categoryId?: string;
  size?: 'sm' | 'md' | 'lg';
  showText?: boolean;
  className?: string;
}

const ShareButton: React.FC<ShareButtonProps> = ({ 
  topicId, 
  topicTitle,
  categoryId,
  size = 'md',
  showText = false,
  className = ''
}) => {
  const { showToast } = useToast();
  const [isCopied, setIsCopied] = useState(false);

  const generateShareUrl = () => {
    const baseUrl = window.location.origin;
    if (categoryId) {
      return `${baseUrl}/community/category/${categoryId}/topic/${topicId}`;
    }
    return `${baseUrl}/community/topic/${topicId}`;
  };

  const handleShare = async () => {
    const shareUrl = generateShareUrl();
    const shareText = `Confira esta discussão: "${topicTitle}"`;

    try {
      // Tentar usar a Web Share API (dispositivos móveis)
      if (navigator.share) {
        await navigator.share({
          title: topicTitle,
          text: shareText,
          url: shareUrl
        });
        showToast('Compartilhado com sucesso!', 'success');
      } else {
        // Fallback: copiar para clipboard
        await navigator.clipboard.writeText(`${shareText}\n\n${shareUrl}`);
        setIsCopied(true);
        showToast('Link copiado para a área de transferência!', 'success');
        
        setTimeout(() => {
          setIsCopied(false);
        }, 2000);
      }
    } catch (error) {
      console.error('Erro ao compartilhar:', error);
      showToast('Erro ao compartilhar', 'error');
    }
  };

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12'
  };

  const iconSizes = {
    sm: 16,
    md: 20,
    lg: 24
  };

  return (
    <button
      onClick={handleShare}
      className={`
        flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200
        bg-gray-700/50 text-gray-400 hover:bg-gray-600/50 hover:text-white
        ${className}
      `}
      title="Compartilhar discussão"
    >
      <div className={`${sizeClasses[size]} flex items-center justify-center`}>
        {isCopied ? (
          <CheckCircle size={iconSizes[size]} className="text-green-400" />
        ) : (
          <Share2 size={iconSizes[size]} />
        )}
      </div>
      {showText && (
        <span className="text-sm font-medium">
          {isCopied ? 'Copiado!' : 'Compartilhar'}
        </span>
      )}
    </button>
  );
};

export default ShareButton; 